import React from 'react';
import {alpha, Box, Chip, Paper, Stack, Typography,} from '@mui/material';
import {Science, Speed, TableChart, WarningAmber,} from '@mui/icons-material';
const aminogramRows = [{
    parameter: 'Histidine', code: 'HIS', measurement: '22.1', reference: '24.1', unit: 'mg/g protein',
}, {
    parameter: 'Isoleucine', code: 'ILE', measurement: '32.7', reference: '30.0', unit: 'mg/g protein',
}, {
    parameter: 'Leucine', code: 'LEU', measurement: '72.5', reference: '59.0', unit: 'mg/g protein',
}, {
    parameter: 'Lysine', code: 'LYS', measurement: '49.1', reference: '45.0', unit: 'mg/g protein',
}, {
    parameter: 'Met + Cys', code: 'SAA', measurement: '18.0', reference: '22.0', unit: 'mg/g protein', limiting: true,
},];
function AminogramVisual() {
    return (<Box
            sx={{
                width: '100%', height: '100%', minHeight: 315, p: 2, bgcolor: 'background.paper', display: 'flex', alignItems: 'center',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper', boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 2, py: 1.45, borderBottom: '1px solid', borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                                width: 30, height: 30, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Science sx={{fontSize: 17}}/>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{fontWeight: 800}}>
                                Aminogram and protein quality
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{display: 'block', mt: 0.25}}
                            >
                                Amino acid profile, PDCAAS and AAS calculation
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                        <Chip
                            label="PDCAAS"
                            size="small"
                            sx={{
                                height: 24, fontSize: 11, fontWeight: 800, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
                            }}
                        />
                        <Chip
                            label="AAS"
                            size="small"
                            sx={{
                                height: 24, fontSize: 11, fontWeight: 800, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08), color: 'text.secondary',
                            }}
                        />
                    </Stack>
                </Stack>

                {/* Body */}
                {/* Body */}
                <Box
                    sx={{
                        display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '0.9fr 1.7fr'}, minHeight: 235,
                    }}
                >
                    {/* Score panel */}
                    <Box
                        sx={{
                            p: 2, borderRight: {sm: '1px solid rgba(15, 23, 42, 0.08)'}, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.018),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block', mb: 1.1, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4,
                            }}
                        >
                            Protein quality score
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.6, borderRadius: 2, bgcolor: 'background.paper', mb: 1.4,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                                <Box
                                    sx={{
                                        width: 28, height: 28, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Speed sx={{fontSize: 16}}/>
                                </Box>

                                <Box>
                                    <Typography variant="caption" sx={{display: 'block', fontWeight: 800}}>
                                        PDCAAS score
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{display: 'block', fontSize: 10.5}}
                                    >
                                        Digestibility-corrected
                                    </Typography>
                                </Box>
                            </Stack>

                            <Typography
                                sx={{
                                    fontSize: 36, lineHeight: 1, fontWeight: 900, color: 'success.main', mb: 0.6,
                                }}
                            >
                                85%
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{display: 'block', fontWeight: 700}}
                            >
                                Digestibility input: 85%
                            </Typography>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.4, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.045),
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <WarningAmber sx={{fontSize: 17, color: 'warning.main', mt: 0.1}}/>
                                <Box>
                                    <Typography variant="caption" sx={{display: 'block', fontWeight: 800}}>
                                        Limiting amino acid
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{display: 'block', lineHeight: 1.35, fontSize: 10.8}}
                                    >
                                        Methionine + cysteine limits the protein quality score.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Box>

                    {/* Aminogram table */}
                    <Box sx={{p: 2}}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{mb: 1.25}}
                        >
                            <Stack direction="row" spacing={0.8} alignItems="center">
                                <TableChart sx={{fontSize: 17, color: 'primary.main'}}/>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.35,
                                    }}
                                >
                                    Aminogram
                                </Typography>
                            </Stack>

                            <Chip
                                label="WHO reference"
                                size="small"
                                sx={{
                                    height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main',
                                }}
                            />
                        </Stack>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid', gridTemplateColumns: '1.15fr 0.55fr 0.8fr 0.8fr 0.7fr', columnGap: 1, px: 1.35, py: 0.9, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04), borderBottom: '1px solid', borderColor: 'divider',
                                }}
                            >
                                {['Parameter', 'Code', 'Value', 'Reference', 'Status'].map((head) => (<Typography
                                        key={head}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{fontWeight: 800, fontSize: 10.3}}
                                    >
                                        {head}
                                    </Typography>))}
                            </Box>

                            {aminogramRows.map((row, index) => (<Box
                                    key={row.parameter}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1.15fr 0.55fr 0.8fr 0.8fr 0.7fr',
                                        columnGap: 1,
                                        alignItems: 'center',
                                        px: 1.35,
                                        py: 0.82,
                                        borderBottom: index !== aminogramRows.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        bgcolor: row.limiting ? (theme) => alpha(theme.palette.warning.main, 0.12) : 'background.paper',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 800, fontSize: 10.8, color: row.limiting ? 'warning.dark' : 'text.primary',
                                        }}
                                    >
                                        {row.parameter}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{fontWeight: 800, fontSize: 10.8}}
                                    >
                                        {row.code}
                                    </Typography>

                                    <Typography variant="caption" sx={{fontWeight: 800, fontSize: 10.8}}>
                                        {row.measurement}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{fontWeight: 800, fontSize: 10.8}}
                                    >
                                        {row.reference}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 800, fontSize: 10.3, color: row.limiting ? 'warning.dark' : 'success.main',
                                        }}
                                    >
                                        {row.limiting ? 'Limiting' : 'OK'}
                                    </Typography>
                                </Box>))}
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>);
}

export default AminogramVisual;